import React from 'react';
import styled from 'styled-components';
import { Input } from '@bynder/design-system';
import useInlineEditing from '../../hooks/useInlineEditing';
import { StyledPageTitle, StyledChapterItemTitle } from '../guides/Styles';

interface Props {
  labelText: string;
  onChange: (text: string) => void;
  children?: React.ReactNode;
  isPage?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}

export const EditableSidebarItem: React.FC<Props> = ({
  labelText,
  onChange,
  children,
  isPage,
  isActive,
  onClick,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const {
    isEditing,
    text,
    setText,
    handleOnFocus,
    handleKeyDown,
    handleOnSubmit,
  } = useInlineEditing({ labelText, onChange });

  React.useEffect(() => {
    if (inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isEditing]);

  const handleChange = (newText: string) => {
    setText(newText);
  };

  if (isEditing) {
    return (
      <InlineInput
        data-testid="editableInput"
        inputRef={inputRef}
        type="text"
        value={text}
        onChange={handleChange}
        onBlur={handleOnSubmit}
        onKeyDown={handleKeyDown}
        placeholder={labelText}
      />
    );
  }

  if (isPage) {
    return (
      <StyledPageTitle
        $isActive={isActive}
        $isDisabled={false}
        onClick={onClick}
        onDoubleClick={handleOnFocus}
        data-testid="pageName"
      >
        {labelText}
        {children}
      </StyledPageTitle>
    );
  }

  return (
    <>
      <StyledChapterItemTitle
        $isHovering={false}
        data-testid="chapterName"
        onDoubleClick={handleOnFocus}
      >
        {labelText}
      </StyledChapterItemTitle>
      {children}
    </>
  );
};

const InlineInput = styled(Input)`
  z-index: 1;
`;
